// Lazily-fetching combobox for config-form fields annotated with
// `gundi:reference` in a runner's ui_schema. See:
//   docs/superpowers/specs/2026-08-03-reference-select-widget-design.md
//   gundi-integration-cmore:docs/rfc-reference-data-portal-support.md
//
// Invariants this widget must never violate:
//   1. The form never blocks on reference data — every failure mode degrades
//      to a plain text input, with a non-intrusive warning.
//   2. Saved config is never auto-mutated — a stored value missing from
//      fetched options gets a warning badge, never cleared or replaced.
//   3. Empty fields fetch lazily (menu-open only); a field that already holds
//      a saved value fetches on mount so the stored (often opaque) ID renders
//      as its display label. Both paths rely on the in-flight/TTL cache in
//      src/api/referenceOptions.ts to keep the fan-out cheap.
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { WidgetProps } from "@rjsf/utils";
import { useTranslation } from "react-i18next";
import { HoverTooltip } from "../HoverTooltip";
import { AttentionIcon } from "../ProjectIcons";
import { defaultStyle } from "../componentstyles/selectinputstyles";
import { idToDataPath, resolveDataRef } from "./referencePath";
import { fetchReferenceOptions, ReferenceDataResponse, ReferenceOption } from "../../../api/referenceOptions";
import { fetchProviderReferenceOptions } from "../../../api/providerTargets";

export type ReferenceAnnotationParam = unknown | { $data: string };

export type ReferenceAnnotation = {
    action: string;
    target?: "self" | "provider";
    params?: Record<string, ReferenceAnnotationParam>;
    allow_free_text?: boolean;
};

export type FlatSelectOption = { value: string; label: string; description?: string | null };
export type GroupedSelectOption = { label: string; options: FlatSelectOption[] };
export type SelectOptions = FlatSelectOption[] | GroupedSelectOption[];

export type ResolvedAnnotationParams = {
    /** Fully resolved params (literals passed through, $data refs resolved against rootFormData). */
    params: Record<string, unknown>;
    /** Keys of params whose $data ref resolved to empty/undefined/null (or couldn't be resolved at all). */
    missing: string[];
};

function isDataRefSpec(value: unknown): value is { $data: string } {
    return typeof value === "object" && value !== null && typeof (value as any).$data === "string";
}

function isEmptyValue(value: unknown): boolean {
    return value === undefined || value === null || value === "";
}

/** Pure decision logic for step 2 of the spec: resolve every `params` entry (literal
 * pass-through or `$data` relative reference), and report which ones came back empty
 * so the caller can gate the fetch. `id` is the field's own rjsf id (e.g.
 * "root_event_type_to_tag_0_field_mappings_1_cmore_field_name"); `rootSchema` /
 * `rootFormData` come from `registry.rootSchema` / `formContext.rootFormData`. */
export function resolveAnnotationParams(
    annotation: ReferenceAnnotation | undefined,
    id: string,
    rootSchema: any,
    rootFormData: any,
): ResolvedAnnotationParams {
    if (!annotation?.params) return { params: {}, missing: [] };

    const fieldPath = idToDataPath(id, rootSchema);
    const params: Record<string, unknown> = {};
    const missing: string[] = [];

    for (const [key, raw] of Object.entries(annotation.params)) {
        if (isDataRefSpec(raw)) {
            const resolved = fieldPath ? resolveDataRef(raw.$data, fieldPath, rootFormData) : undefined;
            if (isEmptyValue(resolved)) missing.push(key);
            params[key] = resolved;
        } else {
            params[key] = raw;
        }
    }

    return { params, missing };
}

/** Pure mapping from fetched options to react-select's option shape: label falls back
 * to value, and options are grouped (react-select's `{label, options}[]` grouped form)
 * as soon as any option carries a `group` — ungrouped options among them land in a
 * trailing untitled group rather than being dropped. */
export function buildSelectOptions(options: ReferenceOption[]): SelectOptions {
    const flat: FlatSelectOption[] = options.map((o) => ({
        value: o.value,
        label: o.label ?? o.value,
        description: o.description,
    }));

    const hasGroup = options.some((o) => !!o.group);
    if (!hasGroup) return flat;

    const groups = new Map<string, FlatSelectOption[]>();
    const ungrouped: FlatSelectOption[] = [];
    options.forEach((o, i) => {
        if (o.group) {
            if (!groups.has(o.group)) groups.set(o.group, []);
            groups.get(o.group)!.push(flat[i]);
        } else {
            ungrouped.push(flat[i]);
        }
    });

    const grouped: GroupedSelectOption[] = Array.from(groups.entries()).map(([label, opts]) => ({ label, options: opts }));
    if (ungrouped.length) grouped.push({ label: "", options: ungrouped });
    return grouped;
}

export function isGroupedOptions(options: SelectOptions): options is GroupedSelectOption[] {
    return options.length > 0 && Array.isArray((options[0] as GroupedSelectOption).options);
}

function flattenOptions(options: SelectOptions): FlatSelectOption[] {
    return isGroupedOptions(options) ? options.flatMap((g) => g.options) : options;
}

/** Step 6 of the spec: a saved value that isn't among the fetched options gets a
 * warning badge — but is never cleared. Only meaningful once a fetch has actually
 * succeeded (callers should not call this against options: [] before the first fetch). */
export function isStaleValue(value: unknown, options: ReferenceOption[]): boolean {
    if (isEmptyValue(value)) return false;
    return !options.some((o) => o.value === value);
}

function toSelectedOption(value: unknown, flat: FlatSelectOption[]): FlatSelectOption | null {
    if (isEmptyValue(value)) return null;
    const stringValue = String(value);
    return flat.find((o) => o.value === stringValue) ?? { value: stringValue, label: stringValue };
}

type FetchState =
    | { status: "not_fetched" }
    | { status: "loading" }
    | { status: "error" }
    | { status: "success"; options: ReferenceOption[]; truncated?: boolean; providerCount?: number };

/** How long a fetch may run before the loading message escalates from
 * "Loading options…" to reassurance that a slow (cold-start) fetch is normal. */
export const SLOW_LOADING_THRESHOLD_MS = 4000;

/** Menu content shown while options are being fetched (react-select
 * `LoadingMessage` override). A fetch can legitimately take several seconds —
 * the runner behind the execute proxy may be cold-starting — so after
 * SLOW_LOADING_THRESHOLD_MS the text escalates to say that explicitly instead
 * of leaving the operator staring at a silent menu. */
export const ReferenceLoadingMessage = () => {
    const { t } = useTranslation();
    const [slow, setSlow] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setSlow(true), SLOW_LOADING_THRESHOLD_MS);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-secondary-gray" role="status">
            {/* Thick-stroke arc spinner: at 16px the standard donut spinner's thin
                arc all but disappears, so this uses a bold rounded arc in full
                brand green over a faint track, spinning slightly faster than
                Tailwind's 1s default to read as clearly active. */}
            <svg
                aria-hidden="true"
                className="w-4 h-4 shrink-0 animate-spin text-dark-green"
                style={{ animationDuration: "0.7s" }}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4.5" />
                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" />
            </svg>
            <span className="animate-pulse">
                {slow
                    ? t("Still working — fetching live options can take a few seconds…")
                    : t("Loading options…")}
            </span>
        </div>
    );
};

const ReferenceSelectWidget = (props: WidgetProps) => {
    const { id, value, disabled, readonly, onChange, registry, uiSchema } = props;
    const TextWidget = registry.widgets.TextWidget;
    const { t } = useTranslation();

    const annotation = uiSchema?.["gundi:reference"] as ReferenceAnnotation | undefined;
    const formContext = (registry.formContext ?? {}) as {
        rootFormData?: any;
        integrationId?: string;
        authToken?: string;
    };
    const { rootFormData, integrationId, authToken } = formContext;

    // Requirement 1: no annotation, or missing formContext wiring → the pre-feature
    // plain-input experience, verbatim. target: "provider" is a full-flow annotation
    // (see fetchProviderReferenceOptions) — it no longer falls back to plain text here;
    // "no provider offers this action" is handled as its own success state below.
    const useDefaultWidget = !annotation || !integrationId || !authToken;
    const isProviderTarget = annotation?.target === "provider";

    const { params, missing } = useMemo(
        () => resolveAnnotationParams(annotation, id, registry.rootSchema, rootFormData),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [annotation, id, registry.rootSchema, rootFormData],
    );
    const paramsKey = JSON.stringify(params);

    const [fetchState, setFetchState] = useState<FetchState>({ status: "not_fetched" });
    const requestIdRef = useRef(0);
    const prevParamsKeyRef = useRef(paramsKey);

    // Requirement 7: when the resolved params change (by JSON identity), drop any
    // fetched options — the next menu-open re-fetches (cheap: TTL cache) — and never
    // touch props.value. The stale-value check itself is derived below, so resetting
    // fetchState to "not_fetched" here automatically re-runs it (no stale badge shows
    // until the next successful fetch, per requirement 6).
    // Bumping requestIdRef here too is load-bearing: without it, a slow fetch that was
    // still in flight for the OLD params would pass the requestId guard in runFetch's
    // .then/.catch when it eventually resolves, overwriting this reset with a
    // "success" (or "error") for params that no longer apply — e.g. pinning a false
    // stale-value badge until the next menu open.
    useEffect(() => {
        if (prevParamsKeyRef.current === paramsKey) return;
        prevParamsKeyRef.current = paramsKey;
        requestIdRef.current++;
        setFetchState({ status: "not_fetched" });
    }, [paramsKey]);

    const runFetch = () => {
        const requestId = ++requestIdRef.current;
        setFetchState({ status: "loading" });
        // target: "provider" resolves the Routes' provider integrations and fans
        // the reference action out to each one (union+dedupe); target: "self" (default)
        // queries the integration being configured. Same fetch lifecycle either way.
        const fetchPromise = isProviderTarget
            ? fetchProviderReferenceOptions(authToken as string, integrationId as string, annotation!.action, params)
            : fetchReferenceOptions(authToken as string, integrationId as string, annotation!.action, params);
        fetchPromise
            .then((resp: ReferenceDataResponse & { providerCount?: number }) => {
                if (requestIdRef.current !== requestId) return; // superseded by a newer fetch
                setFetchState({
                    status: "success",
                    options: resp.options ?? [],
                    truncated: resp.truncated,
                    // Only the provider-target response carries providerCount;
                    // both response shapes are assignable to this widened type,
                    // so no cast is needed.
                    providerCount: resp.providerCount,
                });
            })
            .catch(() => {
                if (requestIdRef.current !== requestId) return;
                setFetchState({ status: "error" });
            });
    };

    // Requirement 3a (amends "lazy" in requirement 3): a field that already
    // carries a saved value fetches as soon as it can — on mount, and again
    // whenever the params-change effect above resets fetchState — so the stored
    // (often opaque) ID renders as its display label without the operator having
    // to open the menu. Empty fields keep the original menu-open-only laziness.
    // The mount-time fan-out is bounded: only fields with values and fully
    // resolved cascade params fetch, and the in-flight/TTL cache in
    // referenceOptions.ts collapses sibling rows sharing (action, params) into
    // one request. The not_fetched guard makes re-runs a no-op once a fetch is
    // loading/succeeded/failed; paramsKey is in the deps so a cascade change
    // (which resets fetchState above) re-triggers the prefetch.
    useEffect(() => {
        if (useDefaultWidget || isEmptyValue(value) || missing.length > 0) return;
        if (fetchState.status !== "not_fetched") return;
        runFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useDefaultWidget, value, missing.length, fetchState.status, paramsKey]);

    if (useDefaultWidget) {
        return <TextWidget {...props} />;
    }

    const handleMenuOpen = () => {
        if (missing.length > 0) return; // never fetch with unresolved cascade params
        if (fetchState.status === "loading") return;
        runFetch();
    };

    const commonSelectProps = {
        inputId: id,
        isDisabled: disabled || readonly,
        isClearable: true,
        // Portal the open menu to <body>: inside the rjsf form each array item
        // establishes its own stacking context, so an in-place menu paints
        // underneath the next item's move/delete controls regardless of its
        // z-index. Portaling escapes those contexts (and any overflow clipping).
        menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
        styles: {
            ...defaultStyle,
            menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
        },
        // Animated, escalating feedback while options load (fetches can take
        // several seconds on a cold-started runner).
        components: { LoadingMessage: ReferenceLoadingMessage },
        onMenuOpen: handleMenuOpen,
        onChange: (option: FlatSelectOption | null) => onChange(option?.value ?? undefined),
    };

    // Requirement 2: any $data param still unresolved → no fetch; free-text combobox
    // (no options to pick from yet) with a hint naming what to fill in first.
    if (missing.length > 0) {
        const hint = t("Select {{fields}} first", { fields: missing.join(", ") });
        return (
            <CreatableSelect
                {...commonSelectProps}
                options={[]}
                value={value ? { value, label: String(value) } : null}
                placeholder={hint}
                noOptionsMessage={() => hint}
                onCreateOption={(input: string) => onChange(input)}
            />
        );
    }

    // Requirement 5: a failed fetch degrades to the plain-input experience (never a
    // dead-end select) plus a warning + retry affordance local to this field.
    if (fetchState.status === "error") {
        return (
            <div>
                <TextWidget {...props} />
                <div className="flex items-center gap-1 mt-1 text-sm text-secondary-gray">
                    <HoverTooltip content={t("Couldn't load options — enter a value manually")}>
                        <span>
                            <AttentionIcon />
                        </span>
                    </HoverTooltip>
                    <button
                        type="button"
                        className="text-dark-green hover:underline"
                        onClick={runFetch}
                    >
                        {t("Retry")}
                    </button>
                </div>
            </div>
        );
    }

    // Provider-resolution spec: "if none has it, the field stays free text" — a
    // successful fetch that resolved zero matching providers is not an error (no
    // warning badge, no retry affordance), just a plain free-text combobox naming why.
    if (isProviderTarget && fetchState.status === "success" && fetchState.providerCount === 0) {
        const hint = t("No connected provider offers suggestions");
        return (
            <CreatableSelect
                {...commonSelectProps}
                options={[]}
                value={value ? { value, label: String(value) } : null}
                placeholder={hint}
                noOptionsMessage={() => hint}
                onCreateOption={(input: string) => onChange(input)}
            />
        );
    }

    const isLoading = fetchState.status === "loading";
    const fetchedOptions = fetchState.status === "success" ? fetchState.options : [];
    const selectOptions = buildSelectOptions(fetchedOptions);
    const flatOptions = flattenOptions(selectOptions);
    const selectedOption = toSelectedOption(value, flatOptions);
    // Requirement 6: only meaningful once a fetch has actually succeeded — before that,
    // fetchedOptions is [] and we must not flag an as-yet-unverified saved value as stale.
    const stale = fetchState.status === "success" && isStaleValue(value, fetchedOptions);

    const allowFreeText = annotation!.allow_free_text !== false;
    const SelectComponent: any = allowFreeText ? CreatableSelect : Select;
    const extraProps = allowFreeText ? { onCreateOption: (input: string) => onChange(input) } : {};

    return (
        <div className="flex items-center gap-1">
            <div className="flex-1 min-w-0">
                <SelectComponent
                    {...commonSelectProps}
                    {...extraProps}
                    options={selectOptions}
                    value={selectedOption}
                    isLoading={isLoading}
                />
            </div>
            {stale && (
                <HoverTooltip content={t("Saved value not in the current list")}>
                    <span>
                        <AttentionIcon />
                    </span>
                </HoverTooltip>
            )}
        </div>
    );
};

export default ReferenceSelectWidget;
